import React from "react";
import { Build as BuildIcon, Layers as LayersIcon } from "@mui/icons-material";
import { Style, Stroke, Fill } from "ol/style";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Draw from "ol/interaction/Draw";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  Paper,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Map as MapIcon,
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  LocationOn as LocationOnIcon,
  ExpandLess,
  ExpandMore,
  AdsClick as AdsClickIcon,
} from "@mui/icons-material";
import PinDropIcon from "@mui/icons-material/PinDrop";

/** select tool drawer panel */
export default function SelectToolPanel(ctx) {
  const {
    selectMode,
    setSelectMode,
    selectToolActive,
    selectedRows,
    setSelectedRows,
    tableViewMode,
    setTableViewMode,
    attributeData,
    attributeColumns,
    updateMapSelectionFromRows,
    queryLayer,
    setQueryLayer,
    layerGroups,
    isDrawingActive,
    mapInstanceRef,
    selectInteractionRef,
    selectDrawLayerRef,
    isDrawingActiveRef,
    setIsDrawingActive,
    fetchFeaturesInGeometry,
    fetchFeaturesInBbox,
    setAttributeTableOpen,
    setAttributeTableDocked,
    selectionLayerRef,
    onCloseTool,
  } = ctx;

  return (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
                  >
                    Select Features
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                      Selection Mode
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={selectMode}
                      onChange={(e) => setSelectMode(e.target.value)}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="rectangle">⬛ Rectangle Select</MenuItem>
                      <MenuItem value="freehand">🔷 Polygon Select</MenuItem>
                    </Select>
                  </Box>

                  <Box
                    sx={{ mb: 2, p: 1.5, bgcolor: "#e3f2fd", borderRadius: 1 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "11px", color: "#1976d2" }}
                    >
                      {selectMode === "rectangle"
                        ? "⬛ Drag on the map to draw a rectangle"
                        : "🔷Click points on the map to draw a polygon, double-click to finish"}
                    </Typography>
                  </Box>

                  {/* Selected Layer */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 0.5, fontWeight: 500 }}
                    >
                      Select Layer
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={queryLayer}
                      onChange={(e) => {
                        setQueryLayer(e.target.value);
                      }}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="">-- Select Layer --</MenuItem>
                      {Object.entries(layerGroups).map(([groupKey, group]) =>
                        Object.entries(group.children).map(
                          ([layerKey, layer]) => (
                            <MenuItem key={layerKey} value={layerKey}>
                              {layer.name}
                            </MenuItem>
                          ),
                        ),
                      )}
                    </Select>
                  </Box>

                  {selectedRows.length > 0 && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1,
                        bgcolor: "#e8f5e9",
                        borderRadius: 1,
                        border: "1px solid #4caf50",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: 11, color: "#2e7d32", fontWeight: 600 }}
                      >
                        ✅ {selectedRows.length} features selected
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    {!isDrawingActive ? (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<BuildIcon />}
                        onClick={() => {
                          if (!mapInstanceRef.current) return;
                          if (!queryLayer) {
                            alert("Please select the layer ");
                            return;
                          }
                          import("ol/interaction/DragBox").then(
                            ({ default: DragBox }) => {
                              import("ol/events/condition").then(
                                (condition) => {
                                  if (selectInteractionRef.current) {
                                    mapInstanceRef.current.removeInteraction(
                                      selectInteractionRef.current,
                                    );
                                  }
                                  const dragBox = new DragBox({
                                    condition:
                                      selectMode === "rectangle"
                                        ? (e) => e.originalEvent.button === 0
                                        : condition.never,
                                  });
                                  if (selectMode === "freehand") {
                                    if (!selectDrawLayerRef.current) {
                                      const src = new VectorSource();
                                      const lyr = new VectorLayer({
                                        source: src,
                                        style: new Style({
                                          stroke: new Stroke({
                                            color: "#ff6600",
                                            width: 2,
                                            lineDash: [5, 5],
                                          }),
                                          fill: new Fill({
                                            color: "rgba(255,102,0,0.1)",
                                          }),
                                        }),
                                        zIndex: 1001,
                                      });
                                      selectDrawLayerRef.current = lyr;
                                      mapInstanceRef.current.addLayer(lyr);
                                    }
                                    const draw = new Draw({
                                      source:
                                        selectDrawLayerRef.current.getSource(),
                                      type: "Polygon",
                                      freehand: false,
                                    });
                                    selectInteractionRef.current = draw;
                                    isDrawingActiveRef.current = true;
                                    setIsDrawingActive(true);
                                    mapInstanceRef.current.addInteraction(draw);
                                    draw.on("drawend", async (evt) => {
                                      // mapInstanceRef.current.removeInteraction(
                                      //   draw,
                                      // );
                                      // isDrawingActiveRef.current = false;
                                      // setIsDrawingActive(false);
                                      const geom = evt.feature.getGeometry();
                                      const cloned = geom
                                        .clone()
                                        .transform("EPSG:3857", "EPSG:4326");
                                      await fetchFeaturesInGeometry(cloned);
                                      setTimeout(() => {
                                        selectDrawLayerRef.current
                                          ?.getSource()
                                          .clear();
                                      }, 1000);
                                    });
                                  } else {
                                    selectInteractionRef.current = dragBox;
                                    isDrawingActiveRef.current = true;
                                    setIsDrawingActive(true);
                                    mapInstanceRef.current.addInteraction(
                                      dragBox,
                                    );
                                    dragBox.on("boxend", async () => {
                                      // mapInstanceRef.current.removeInteraction(
                                      //   dragBox,
                                      // );
                                      // isDrawingActiveRef.current = false;
                                      // setIsDrawingActive(false);
                                      const extent = dragBox
                                        .getGeometry()
                                        .getExtent();
                                      import("ol/proj").then(
                                        ({ transformExtent }) => {
                                          const extent4326 = transformExtent(
                                            extent,
                                            "EPSG:3857",
                                            "EPSG:4326",
                                          );
                                          fetchFeaturesInBbox(extent4326);
                                        },
                                      );
                                    });
                                  }
                                },
                              );
                            },
                          );
                        }}
                        sx={{
                          bgcolor: "#1976d2",
                          "&:hover": { bgcolor: "#115293" },
                        }}
                      >
                        Start Selecting
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          if (
                            selectInteractionRef.current &&
                            mapInstanceRef.current
                          ) {
                            mapInstanceRef.current.removeInteraction(
                              selectInteractionRef.current,
                            );
                            selectInteractionRef.current = null;
                          }
                          isDrawingActiveRef.current = false;
                          setIsDrawingActive(false);
                          selectDrawLayerRef.current?.getSource().clear();
                        }}
                        sx={{
                          bgcolor: "#ff9800",
                          "&:hover": { bgcolor: "#f57c00" },
                        }}
                      >
                        Stop Selecting
                      </Button>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.8,
                        mt: "auto",
                        flexShrink: 0,
                        mb: 2,
                      }}
                    >
                      {selectedRows.length > 0 && (
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<LayersIcon />}
                          onClick={() => {
                            setAttributeTableOpen(true);
                            setAttributeTableDocked(true);
                            setTableViewMode("selected");
                          }}
                          sx={{ borderColor: "#4caf50", color: "#4caf50" }}
                        >
                          View in Attribute Table
                        </Button>
                      )}
                      <Box sx={{ display: "flex", gap: 0.8 }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedRows([]);
                            if (selectionLayerRef.current) {
                              selectionLayerRef.current.getSource().clear();
                              selectionLayerRef.current.changed();
                            }
                          }}
                          disabled={selectedRows.length === 0}
                          sx={{ borderColor: "#ff9800", color: "#ff9800" }}
                        >
                          Clear
                        </Button>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          onClick={() => {
                            onCloseTool("select");
                            if (
                              selectInteractionRef.current &&
                              mapInstanceRef.current
                            ) {
                              mapInstanceRef.current.removeInteraction(
                                selectInteractionRef.current,
                              );
                            }
                            isDrawingActiveRef.current = false;
                            setIsDrawingActive(false);
                          }}
                          sx={{
                            borderColor: "#f44336",
                            color: "#f44336",
                            "&:hover": { bgcolor: "#ffebee" },
                          }}
                        >
                          Close
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
  );
}
